// Copyright ©️ 2026 eVolve MEP, LLC

import React from 'react';

import { type Project, useEvolvePost } from '@evolvemep/api-ts';
import { Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';

export const OpenCloseProjectModal = ({
  project,
  opened,
  onClose,
  onProjectUpdated,
}: {
  project: Project;
  onProjectUpdated: (updatedProject: Project) => void;
  opened: boolean;
  onClose: () => void;
}) => {
  const { apiCall: closeCall, loading: closing } = useEvolvePost(`shop/project/${project.projectId}/close`);
  const { apiCall: activateCall, loading: activating } = useEvolvePost(`shop/project/${project.projectId}/activate`);
  const saving = closing || activating;

  const onSubmit = async () => {
    const res = project.isClosed ? await activateCall({}) : await closeCall({});
    notifications.show({
      title: 'Successfully updated',
      message: `Project ${res.projectName} was successfully ${res.isClosed ? 'closed' : 'activated'}.`,
      color: 'green',
    });
    onProjectUpdated(res);
  };

  return (
    <WrappedModal
      opened={opened}
      onClose={onClose}
      onConfirm={onSubmit}
      title={project.isClosed ? 'Activate project' : 'Archive project'}
      loading={saving}
      confirmationText={project.isClosed ? 'Activate' : 'Archive'}
      alertState={{
        title: 'Cannot archive project',
        showAlert: project.hasActiveWorkOrders && !project.isClosed,
        message: (
          <Text>
            <b>{project.projectName}</b> has active work orders and can not be archived.
          </Text>
        ),
      }}
    >
      <Text>
        Are you sure you want to {project.isClosed ? 'activate' : 'archive'} <b>{project.projectName}</b>?
      </Text>
    </WrappedModal>
  );
};
